// fields/MultiSelectSearchField.js
// ============================================
// Searchable multi-select field with local data
// ============================================
import React, { useState, useEffect, useRef } from 'react';

export const MultiSelectSearchField = ({ 
  field, 
  register, 
  errors, 
  theme, 
  setValue, 
  watch,
  options = [], // Array of { id, displayName, info }
  placeholder = "Search and select multiple...",
  displayFormat = "full", // "full", "nameOnly", "custom"
  customDisplay = null, // Custom display function
  allowClear = true,
  maxDropdownHeight = "300px",
  maxTags = null, // Maximum number of selections allowed
  tagVariant = "default", // "default", "compact", "minimal"
  showSelectedCount = true, // Show count when collapsed
  collapseTags = true, // Collapse tags after certain number
  collapseAfter = 3, // Number of tags to show before collapsing
  hideDropdownOnSelect = false, // Keep dropdown open after selection
  selectAllOption = true, // Show "Select All" option
  searchMinLength = 0, // Minimum characters before search
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedItems, setSelectedItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const fieldTheme = theme.fields?.multiSelectSearch || theme.fields?.selectSearch || theme.fields?.select || {};
  const dropdownTheme = theme.dropdown || {};
  const tagTheme = theme.tags || {};
  const currentValue = watch(field.id) || [];
  
  // Initialize selected items from form value
  useEffect(() => {
    if (Array.isArray(currentValue) && options.length > 0) {
      const items = options.filter(opt => currentValue.includes(opt.id));
      setSelectedItems(items);
    }
  }, [currentValue, options]);
  
  // Filter options based on search term
  useEffect(() => {
    if (searchTerm && searchTerm.length >= searchMinLength) {
      const filtered = options.filter(option => {
        const searchLower = searchTerm.toLowerCase();
        return (
          option.displayName.toLowerCase().includes(searchLower) ||
          (option.info && option.info.toLowerCase().includes(searchLower)) ||
          (option.id && option.id.toString().toLowerCase().includes(searchLower))
        );
      });
      setFilteredOptions(filtered);
      setHighlightedIndex(0);
    } else {
      setFilteredOptions(options);
      setHighlightedIndex(0);
    }
  }, [searchTerm, options, searchMinLength]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsTagsExpanded(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle backspace to remove last selected item
    if (e.key === 'Backspace' && searchTerm === '' && selectedItems.length > 0 && !field.disabled) {
      e.preventDefault();
      const newItems = selectedItems.slice(0, -1);
      handleSelectionChange(newItems);
      return;
    }
    
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const maxIndex = selectAllOption ? filteredOptions.length : filteredOptions.length - 1;
          return prev < maxIndex ? prev + 1 : prev;
        });
        scrollToHighlighted();
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        scrollToHighlighted();
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectAllOption && highlightedIndex === 0) {
          handleSelectAll();
        } else {
          const adjustedIndex = selectAllOption ? highlightedIndex - 1 : highlightedIndex;
          if (filteredOptions[adjustedIndex]) {
            handleToggleSelect(filteredOptions[adjustedIndex]);
          }
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
        
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  
  const scrollToHighlighted = () => {
    setTimeout(() => {
      const highlighted = dropdownRef.current?.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  };
  
  const handleToggleSelect = (option) => {
    const isSelected = selectedItems.some(item => item.id === option.id);
    let newItems;
    
    if (isSelected) {
      newItems = selectedItems.filter(item => item.id !== option.id);
    } else {
      if (maxTags && selectedItems.length >= maxTags) {
        return; // Don't add if max reached
      }
      newItems = [...selectedItems, option];
    }
    
    handleSelectionChange(newItems);
    
    if (!hideDropdownOnSelect) {
      setSearchTerm('');
    } else {
      setIsOpen(false);
    }
  };
  
  const handleSelectionChange = (newItems) => {
    setSelectedItems(newItems);
    const ids = newItems.map(item => item.id);
    setValue(field.id, ids, { shouldValidate: true });
  };
  
  const handleSelectAll = () => {
    const allFiltered = filteredOptions.slice(0, maxTags || filteredOptions.length);
    const allSelected = allFiltered.every(opt => 
      selectedItems.some(item => item.id === opt.id)
    );
    
    if (allSelected) {
      // Deselect all filtered items
      const filteredIds = allFiltered.map(opt => opt.id);
      const newItems = selectedItems.filter(item => !filteredIds.includes(item.id));
      handleSelectionChange(newItems);
    } else {
      // Select all filtered items (up to max)
      const existingIds = selectedItems.map(item => item.id);
      const newItemsToAdd = allFiltered.filter(opt => !existingIds.includes(opt.id));
      const combinedItems = [...selectedItems, ...newItemsToAdd];
      const finalItems = maxTags ? combinedItems.slice(0, maxTags) : combinedItems;
      handleSelectionChange(finalItems);
    }
  };
  
  const handleRemoveItem = (itemToRemove, e) => {
    e.stopPropagation();
    const newItems = selectedItems.filter(item => item.id !== itemToRemove.id);
    handleSelectionChange(newItems);
  };
  
  const handleClearAll = (e) => {
    e.stopPropagation();
    handleSelectionChange([]);
    setSearchTerm('');
    setIsTagsExpanded(false);
  };
  
  const handleInputClick = () => {
    setIsOpen(true);
    inputRef.current?.focus();
  };
  
  const getDisplayText = (item) => {
    if (!item) return '';
    
    if (customDisplay) {
      return customDisplay(item);
    }
    
    switch (displayFormat) {
      case 'nameOnly':
        return item.displayName;
      case 'full':
        return item.info ? `${item.displayName} - ${item.info}` : item.displayName;
      default:
        return item.displayName;
    }
  };
  
  const getInputClassName = () => {
    const classes = [
      fieldTheme.base || theme.input,
      'cursor-pointer'
    ];
    
    if (isFocused) classes.push(fieldTheme.focus);
    if (errors[field.id]) classes.push(fieldTheme.error || theme.inputError);
    if (field.disabled) classes.push(fieldTheme.disabled);
    if (selectedItems.length > 0) classes.push(fieldTheme.filled);
    
    return classes.join(' ');
  };
  
  const getItemClassName = (index, option = null) => {
    const isHighlighted = index === highlightedIndex;
    const isSelected = option && selectedItems.some(item => item.id === option.id);
    
    if (isSelected && dropdownTheme.itemSelected) {
      return dropdownTheme.itemSelected;
    } else if (isHighlighted) {
      return dropdownTheme.itemHighlighted || 'px-3 py-2 cursor-pointer transition-colors bg-blue-50 dark:bg-blue-900/30';
    } else {
      return dropdownTheme.item || 'px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700';
    }
  };
  
  const getTagClassName = () => {
    const baseClasses = tagTheme.base || 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
    
    switch (tagVariant) {
      case 'compact':
        return tagTheme.compact || 'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
      case 'minimal':
        return tagTheme.minimal || 'inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      default:
        return baseClasses;
    }
  };
  
  // Determine which tags to show
  const visibleTags = collapseTags && !isTagsExpanded && selectedItems.length > collapseAfter
    ? selectedItems.slice(0, collapseAfter)
    : selectedItems;
  const hiddenCount = selectedItems.length - visibleTags.length;
  
  // Check if all filtered items are selected
  const allFilteredSelected = filteredOptions.length > 0 && 
    filteredOptions.every(opt => selectedItems.some(item => item.id === opt.id));
  
  return (
    <div className="relative" ref={containerRef}>
      {/* Selected Tags Display */}
      {selectedItems.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5 items-center">
          {visibleTags.map(item => (
            <span key={item.id} className={getTagClassName()}>
              <span className="truncate max-w-[200px]">
                {tagVariant === 'minimal' ? item.displayName : getDisplayText(item)}
              </span>
              {!field.disabled && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveItem(item, e)}
                  className="ml-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          
          {/* Show more/less button */}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setIsTagsExpanded(!isTagsExpanded)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              +{hiddenCount} more
            </button>
          )}
          
          {/* Clear all button */}
          {allowClear && !field.disabled && selectedItems.length > 1 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-red-600 dark:text-red-400 hover:underline ml-2"
            >
              Clear all
            </button>
          )}
        </div>
      )}
      
      <div className="relative">
        {/* Search Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onClick={handleInputClick}
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={selectedItems.length > 0 
            ? `${selectedItems.length} selected${maxTags ? ` (max ${maxTags})` : ''}` 
            : placeholder}
          className={getInputClassName()}
          disabled={field.disabled}
          autoComplete="off"
        />
        
        {/* Icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Clear button */}
          {allowClear && selectedItems.length > 0 && !field.disabled && (
            <button
              type="button"
              onClick={handleClearAll}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {/* Selected count badge */}
          {showSelectedCount && selectedItems.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full">
              {selectedItems.length}
            </span>
          )}
          
          {/* Dropdown arrow */}
          <svg 
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className={dropdownTheme.container || 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-auto'}
          style={{ maxHeight: maxDropdownHeight }}
        >
          {filteredOptions.length > 0 ? (
            <>
              {/* Select All Option */}
              {selectAllOption && filteredOptions.length > 1 && (
                <div
                  data-highlighted={highlightedIndex === 0}
                  onClick={handleSelectAll}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  className={getItemClassName(0)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={() => {}}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-medium">
                        {allFilteredSelected ? 'Deselect All' : 'Select All'}
                        {searchTerm && ` (${filteredOptions.length})`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Divider after Select All */}
              {selectAllOption && filteredOptions.length > 1 && (
                <div className="border-t border-gray-200 dark:border-gray-700" />
              )}
              
              {/* Options */}
              {filteredOptions.map((option, index) => {
                const actualIndex = selectAllOption ? index + 1 : index;
                const isSelected = selectedItems.some(item => item.id === option.id);
                const isDisabled = maxTags && selectedItems.length >= maxTags && !isSelected;
                
                return (
                  <div
                    key={option.id}
                    data-highlighted={actualIndex === highlightedIndex}
                    onClick={() => !isDisabled && handleToggleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(actualIndex)}
                    className={`${getItemClassName(actualIndex, option)} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          disabled={isDisabled}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className={dropdownTheme.itemLabel || 'font-medium text-gray-900 dark:text-gray-100'}>
                            {option.displayName}
                          </div>
                          {option.info && (
                            <div className={dropdownTheme.itemInfo || 'text-sm text-gray-500 dark:text-gray-400 mt-0.5'}>
                              {option.info}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className={dropdownTheme.noResultsText || 'px-3 py-2 text-gray-500 dark:text-gray-400 text-center'}>
              {searchTerm ? `No results found for "${searchTerm}"` : 'No options available'}
            </div>
          )}
          
          {/* Max selection message */}
          {maxTags && selectedItems.length >= maxTags && (
            <div className="px-3 py-2 text-sm text-orange-600 dark:text-orange-400 border-t border-gray-200 dark:border-gray-700">
              Maximum {maxTags} items can be selected
            </div>
          )}
        </div>
      )}
      
      {/* Hidden input for form value */}
      <input
        type="hidden"
        {...register(field.id, field.validation)}
        value={selectedItems.map(item => item.id).join(',')}
      />
    </div>
  );
};